import { container } from 'tsyringe'
import Vue from 'vue'
import {
  VuexModule,
  Module,
  Mutation,
  Action,
  getModule,
} from 'vuex-module-decorators'
import store from '@/app/ui/store'
import { DropBoxPresenter } from '@/app/ui/presenters/DropBoxPresenter'
import {
  DropBox,
  DropBoxes,
  DropBoxMonitoringSummary,
} from '@/domain/entities/DropBox'
import { Pagination } from '@/domain/entities/Pagination'
import { DROP_BOX_PAGINATION } from '@/app/infrastructures/misc/Constants'
import {
  CreateDropBoxRequest,
  UpdateDropBoxRequest,
} from '@/data/payload/api/DropBoxRequest'

export interface DropBoxState {
  isLoading: boolean
  dropBoxData: DropBox[]
  paginationData: Pagination
  monitoringSummary: DropBoxMonitoringSummary | null
  createSuccess: boolean
  dropBoxDetail: DropBox | null
  updateSuccess: boolean
  deleteSuccess: boolean
}

@Module({ namespaced: true, store, name: 'drop-box', dynamic: true })
class DropBoxController extends VuexModule implements DropBoxState {
  private presenter: DropBoxPresenter = container.resolve(DropBoxPresenter)
  public isLoading = false
  public dropBoxData: DropBox[] = []
  public paginationData = new Pagination(1, DROP_BOX_PAGINATION, 0)
  public monitoringSummary: DropBoxMonitoringSummary | null = null
  public createSuccess = false
  public dropBoxDetail: DropBox | null = null
  public updateSuccess = false
  public deleteSuccess = false

  @Action({ rawError: true })
  public getDropBoxList(
    params: Record<string, string | number | boolean | null | undefined>
  ) {
    this.setLoading(true)
    // Convert params to Map with snake_case keys (already in correct format from view)
    const formattedParams = new Map<string, string | number | boolean>()
    Object.keys(params).forEach((key) => {
      const val = params[key]
      if (val !== null && val !== undefined && val !== '') {
        formattedParams.set(key, val as string | number | boolean)
      }
    })

    this.presenter
      .getAll(formattedParams)
      .then((res) => {
        this.setDropBoxData(res)
      })
      .catch((error) => {
        Vue.notify({
          title: 'Fetch Drop Box List Error',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public getDropBoxMonitoring(
    params: Record<string, string | number | boolean | null | undefined>
  ) {
    this.setLoading(true)
    // Convert params to Map with snake_case keys
    const formattedParams = new Map<string, string | number | boolean>()
    Object.keys(params).forEach((key) => {
      const val = params[key]
      if (val !== null && val !== undefined && val !== '') {
        formattedParams.set(key, val as string | number | boolean)
      }
    })

    this.presenter
      .getMonitoring(formattedParams)
      .then((res) => {
        this.setDropBoxData(res)
      })
      .catch((error) => {
        Vue.notify({
          title: 'Fetch Drop Box Monitoring Error',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public getDropBoxDetail(externalId: string) {
    this.setLoading(true)
    this.setDropBoxDetail(null)

    this.presenter
      .get(externalId)
      .then((res) => {
        this.setDropBoxDetail(res)
      })
      .catch((error) => {
        Vue.notify({
          title: 'Fetch Drop Box Detail Error',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public createDropBox(form: {
    name: string
    serialNumber: string
    lat: number
    long: number
    address: string
    phoneNumber: string
    picAgentCode: string
    visibility: string
  }) {
    this.setLoading(true)
    this.setCreateSuccess(false)

    const payload = new CreateDropBoxRequest(
      form.name,
      form.serialNumber,
      form.lat,
      form.long,
      form.address,
      form.phoneNumber,
      form.picAgentCode,
      form.visibility
    )

    this.presenter
      .create(payload)
      .then(() => {
        this.setCreateSuccess(true)
        Vue.notify({
          title: 'Success',
          text: 'Drop Box successfully created',
          type: 'success',
          duration: 5000,
        })
      })
      .catch((error) => {
        Vue.notify({
          title: 'Create Drop Box Failed',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public updateDropBox(form: {
    externalId: string
    name: string
    serialNumber: string
    lat: number
    long: number
    address: string
    phoneNumber: string
    picAgentCode: string
    visibility: string
  }) {
    this.setLoading(true)
    this.setUpdateSuccess(false)

    const payload = new UpdateDropBoxRequest(
      form.externalId,
      form.name,
      form.serialNumber,
      form.lat,
      form.long,
      form.address,
      form.phoneNumber,
      form.picAgentCode,
      form.visibility
    )

    this.presenter
      .update(payload)
      .then(() => {
        this.setUpdateSuccess(true)
        Vue.notify({
          title: 'Success',
          text: 'Drop Box successfully updated',
          type: 'success',
          duration: 5000,
        })
      })
      .catch((error) => {
        Vue.notify({
          title: 'Update Drop Box Failed',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public deleteDropBox(externalId: string) {
    this.setLoading(true)
    this.setDeleteSuccess(false)

    this.presenter
      .delete(externalId)
      .then(() => {
        this.setDeleteSuccess(true)
        Vue.notify({
          title: 'Success',
          text: 'Drop Box deleted successfully',
          type: 'success',
          duration: 5000,
        })
      })
      .catch((error) => {
        let errorMessage = 'Something wrong'

        // Handle different error status codes
        if (
          error.status === 400 ||
          error.status === 422 ||
          error.status === 404
        ) {
          errorMessage = error.error?.message?.en || 'Drop Box not found'
        } else if (error.status === 403) {
          errorMessage = 'You do not have permission to delete this Drop Box'
        } else if (error.status === 500) {
          errorMessage = 'Server error occurred. Please try again later'
        } else if (error.error?.message?.en) {
          // Fallback: use error message if available regardless of status
          errorMessage = error.error.message.en
        } else if (error.data?.message?.en) {
          // Alternative error structure
          errorMessage = error.data.message.en
        }

        Vue.notify({
          title: 'Delete Drop Box Failed',
          text: errorMessage,
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Mutation
  private setDropBoxData(data: DropBoxes) {
    this.paginationData = data.pagination as Pagination
    this.dropBoxData = data.data as DropBox[]
    this.monitoringSummary = data.summary ?? null
  }

  @Mutation
  public setDropBoxDetail(data: DropBox | null) {
    this.dropBoxDetail = data
  }

  @Mutation
  public setLoading(bool: boolean) {
    this.isLoading = bool
  }

  @Mutation
  public setCreateSuccess(bool: boolean) {
    this.createSuccess = bool
  }

  @Mutation
  public setUpdateSuccess(bool: boolean) {
    this.updateSuccess = bool
  }

  @Mutation
  public setDeleteSuccess(bool: boolean) {
    this.deleteSuccess = bool
  }
}

export default getModule(DropBoxController)
